import { useEffect, useRef, useState } from 'react';
import _map from 'lodash/map';
import { ReactComponent as Apple } from '../../../assets/svg/apple.svg';
import { ReactComponent as Facebook } from '../../../assets/svg/facebook.svg';
import { ReactComponent as Google } from '../../../assets/svg/google.svg';
import { ReactComponent as RoundedEmail } from '../../../assets/svg/roundedEmail.svg';
import SocialAuthProvider from 'constants/socialAuthProvider';
import { Spinner } from 'react-bootstrap';
import StandardPopup from 'component/StandardPopup';
import { STRINGS, ToastType } from '../../../constants';
import { ReactComponent as CopyIcon } from 'assets/svg/copyIcon.svg';
import { copyToClipboardAsync } from 'utils/share';
import SuccessIcon from '@material-ui/icons/CheckCircleOutline';
import { detect } from 'detect-browser';
import constant, { SESSION_STORAGE_KEY } from 'utils/constant';
import { TRACK_EVENT, trackEvent } from 'utils/analytics';
import { makeStyles } from '@material-ui/core';
import themes from 'styles/themes';
import { useLocation } from 'react-router-dom';
import { PATH } from 'routes/constant';
import utils from 'utils/utils';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

export const useStyles = makeStyles((theme) => ({
    copyMsg: {
        backgroundColor: themes.palette.primary.light,
        fontFamily: 'PoppinsRegular',
        fontStyle: 'normal',
        textAlign: 'center',
        color: themes.palette.primary.main,
        padding: '10px',
        width: '100%',
    },
    noneCopyMsg: {
        visibility: 'hidden',
        padding: '10px',
        width: '100%',
    },
    successIcon: {
        color: themes.palette.primary.main,
        marginRight: 5,
    },
    copyMessageContainer: {
        width: '100%',
        padding: '5px',
    },
}));

export default function SocialAuth(props) {
    const classes = useStyles();
    const { handleSocialLogin, styles, mode, loading } = props;
    const popupCustomFooter = (isCopied = false) => {
        return (
            <div className={classes.copyMessageContainer}>
                <div className={isCopied ? classes.copyMsg : classes.noneCopyMsg}>
                    <SuccessIcon className={classes.successIcon} />
                    {STRINGS.LINK_COPIED}
                </div>
            </div>
        );
    };
    const [modalShow, setModalShow] = useState({
        show: false,
        title: STRINGS.UNSUPPORTED_BROWSER,
        content: <></>,
        customFooter: popupCustomFooter(false),
        path: '',
        type: '',
        closeButton: true,
    });
    const copyLink = useRef('');
    const location = useLocation();
    const storedLocationStr = sessionStorage.getItem(SESSION_STORAGE_KEY.FROM);
    const storeSourceUrl = useSelector((state: RootState) => state.widget?.sourceUrl);

    useEffect(() => {
        if (Boolean(utils.isAppInWidgetMode() && storeSourceUrl?.length) || !utils.isAppInWidgetMode()) {
            if (storedLocationStr) {
                const storedLocationObj = JSON.parse(storedLocationStr);
                copyLink.current = utils.generateUrlFromPath(storedLocationObj?.from?.pathname);
            } else {
                copyLink.current = utils.generateUrlFromPath(location?.pathname);
            }
        }
    }, [storedLocationStr, location?.pathname, storeSourceUrl]);

    const browser = detect();
    const browserName = browser?.name || '';
    const authType =
        location.pathname === PATH.SIGNUP_CUSTOMER ? STRINGS.SIGN_UP.toLowerCase() : STRINGS.LOGIN.toLowerCase();

    const currentSocialAuthProvider = useRef('');

    let labelText = 'Log in';
    if (mode === 'signUp') {
        labelText = 'Sign up';
    }

    const handleButtonClick = (provider) => {
        currentSocialAuthProvider.current = provider;
        if (
            constant.UNSUPPORTED_BROWSERS_FOR_SOCIAL_AUTH.includes(browserName) &&
            provider !== SocialAuthProvider.EMAIL
        ) {
            handleBrowserSupportPopup();
            handleAnalytics(false);
        } else {
            handleSocialLogin(provider);
        }
    };

    const handleClickCopyToClipboard = (copyLink) => {
        copyToClipboardAsync(copyLink)
            .then(() => {
                setModalShow((prevModalShow) => {
                    return {
                        ...prevModalShow,
                        customFooter: popupCustomFooter(true),
                    };
                });
                handleAnalytics(true);
                setTimeout(() => {
                    setModalShow((prevModalShow) => {
                        return {
                            ...prevModalShow,
                            customFooter: popupCustomFooter(false),
                        };
                    });
                }, 1500);
            })
            .catch(() => {
                handleClickCopyToClipboard(copyLink);
            });
    };

    const handleAnalytics = (isLinkCopied = false) => {
        trackEvent(TRACK_EVENT.SOCIAL_AUTH_UNSUPPORTED_POPUP, {
            isOpened: true,
            isLinkCopied: isLinkCopied,
            browser: browserName,
            url: copyLink?.current,
            authProvider: currentSocialAuthProvider.current,
            authType,
        });
    };

    const popupContent = () => {
        return (
            <div>
                <div>
                    This browser does not support {authType} using{' '}
                    {utils.capitalizeString(currentSocialAuthProvider?.current, true)}. Please copy the URL you are
                    trying to access and open it with your default browser like Safari or Chrome.
                </div>
                <div className={styles.urlCopyContainer}>
                    <div className={styles.copyLinkMessage}>{copyLink?.current}</div>
                    <div
                        className={styles.copyIconContainer}
                        onClick={() => handleClickCopyToClipboard(copyLink?.current)}
                    >
                        <CopyIcon className={styles.copyIcon} />
                    </div>
                </div>
            </div>
        );
    };

    const handleBrowserSupportPopup = () => {
        setModalShow({
            ...modalShow,
            show: true,
            content: popupContent(),
            type: ToastType.Error,
        });
    };

    const socialAuthProviders = [
        {
            Provider: Google,
            label: `${labelText} with Google`,
            onClick: () => handleButtonClick(SocialAuthProvider.GOOGLE),
            className: styles.cursorPointer,
            providerId: 'google.com',
            method: SocialAuthProvider.GOOGLE,
        },
        {
            Provider: Facebook,
            label: `${labelText} with Facebook`,
            onClick: () => handleButtonClick(SocialAuthProvider.FACEBOOK),
            className: styles.cursorPointer,
            providerId: 'facebook.com',
            method: SocialAuthProvider.FACEBOOK,
        },
        {
            Provider: Apple,
            label: `${labelText} with Apple`,
            onClick: () => handleButtonClick(SocialAuthProvider.APPLE),
            className: styles.cursorPointer,
            providerId: 'apple.com',
            method: SocialAuthProvider.APPLE,
        },
        {
            Provider: RoundedEmail,
            label: `${labelText} with Email`,
            onClick: () => handleButtonClick(SocialAuthProvider.EMAIL),
            className: styles.cursorPointer,
            providerId: 'password',
            method: SocialAuthProvider.EMAIL,
        },
    ];
    return (
        <div>
            <div className="d-flex justify-content-center">
                <div className="d-flex flex-column">
                    {_map(socialAuthProviders, (item, index) => {
                        const { Provider, label, method } = item;
                        return (
                            <button
                                className={`rounded-pill mt-3 d-flex justify-content-start align-items-center w-100 bg-light pe-3 text-dark`}
                                style={{
                                    opacity: loading ? 0.8 : 1,
                                    fontWeight: 500,
                                    fontFamily: 'PoppinsRegular',
                                    border: 0,
                                }}
                                key={index}
                                onClick={item.onClick}
                            >
                                <Provider />
                                {label}
                                <Spinner
                                    animation="border"
                                    style={{
                                        width: 18,
                                        height: 18,
                                        visibility:
                                            loading && currentSocialAuthProvider.current === method
                                                ? 'visible'
                                                : 'hidden',
                                        marginLeft: '10px',
                                    }}
                                />
                            </button>
                        );
                    })}
                </div>
            </div>
            <StandardPopup
                show={modalShow.show}
                onHide={() => {
                    setModalShow({ ...modalShow, show: false });
                }}
                title={modalShow.title}
                content={modalShow.content}
                type={modalShow.type as any}
                closeButton={modalShow.closeButton}
                customFooter={modalShow.customFooter}
            />
        </div>
    );
}
